import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useState } from 'react';

import {
  CUSTOM_LABEL,
  PRESETS,
  presetRange,
  validateCustom,
  type Preset,
  type ResolvedRange,
} from './dateRange';

interface DateRangeSelectProps {
  /** The range the page is showing: always a valid one. */
  value: ResolvedRange;
  /** Called only with a range that can be shown, as concrete dates. */
  onChange: (range: ResolvedRange) => void;
  /** `YYYY-MM-DD`. Presets end on it, and no custom day may be after it. */
  today: string;
}

/**
 * The dashboard's range: three fixed spans, or Custom with From and To.
 *
 * While a custom pair is incomplete or makes no sense, the reason is said
 * against the field at fault and nothing is reported, so the page keeps
 * showing the last range that worked instead of going blank.
 */
export function DateRangeSelect({ value, onChange, today }: DateRangeSelectProps) {
  // What the select shows. Custom can be chosen before there is a custom range
  // to report, so this is not always `value.preset`.
  const [choice, setChoice] = useState<Preset>(value.preset);
  // What is typed into the two fields, which may not make sense yet.
  const [draft, setDraft] = useState({ from: value.from, to: value.to });

  // A new range from outside - the back button, a link - replaces whatever was
  // being chosen or typed. Adjusted while rendering so nothing stale is shown.
  // Compared by its parts: the page reads the range from the address on every
  // render, so it is a new object each time even when it is the same range.
  const key = `${value.preset} ${value.from} ${value.to}`;
  const [seen, setSeen] = useState(key);
  if (key !== seen) {
    setSeen(key);
    setChoice(value.preset);
    setDraft({ from: value.from, to: value.to });
  }

  const problem =
    choice === 'custom' ? validateCustom(draft.from, draft.to, today) : null;

  function choose(next: Preset) {
    setChoice(next);
    if (next === 'custom') {
      // Starts from the range already shown, so Custom is an edit of it rather
      // than two blank boxes. Nothing is reported until a day is changed.
      setDraft({ from: value.from, to: value.to });
      return;
    }
    onChange(presetRange(next, today));
  }

  function changeDay(part: 'from' | 'to', day: string) {
    const next = { ...draft, [part]: day };
    setDraft(next);
    if (validateCustom(next.from, next.to, today) === null) {
      onChange({ preset: 'custom', ...next });
    }
  }

  function dayField(part: 'from' | 'to', label: string) {
    const fault = problem?.field === part ? problem.message : undefined;
    return (
      <TextField
        type="date"
        size="small"
        label={label}
        value={draft[part]}
        onChange={(event) => changeDay(part, event.target.value)}
        error={Boolean(fault)}
        // TextField ties the helper text to the input with aria-describedby
        // and marks the input invalid, so the reason is read with the field.
        helperText={fault}
        slotProps={{
          // The label sits above the box: a date input always shows its own
          // placeholder, and a label on top of that would overlap it.
          inputLabel: { shrink: true },
          htmlInput: { max: today },
          formHelperText: fault ? { role: 'alert' } : undefined,
        }}
        sx={{ width: { xs: '100%', sm: 170 } }}
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        width: { xs: '100%', sm: 'auto' },
      }}
    >
      <TextField
        select
        size="small"
        label="Date range"
        value={choice}
        onChange={(event) => choose(event.target.value as Preset)}
        sx={{ width: { xs: '100%', sm: 170 } }}
      >
        {PRESETS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
        <MenuItem value="custom">{CUSTOM_LABEL}</MenuItem>
      </TextField>

      {choice === 'custom' ? (
        <>
          {dayField('from', 'From')}
          {dayField('to', 'To')}
        </>
      ) : null}
    </Box>
  );
}
